import {
  Button,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CellTimestamp } from '$components/cell-timestamp/cell-timestamp';
import { downloadReport } from '$features/report-hub/report-hub.api';

import { useDashboardReports } from '../dashboard.api';

import { DashboardCardTitle } from './dashboard-card-title';
import { DashboardWidget } from './dashboard-widget';
import { SectionFeedback } from './section-feedback';
import { TruncatedText } from './truncated-text';

import './latest-reports-card.scss';

interface LatestReportsCardProps {
  canReadStabilityReports: boolean;
  canReadAuditTrailReports: boolean;
}

export function LatestReportsCard({
  canReadStabilityReports,
  canReadAuditTrailReports,
}: LatestReportsCardProps) {
  const { t } = useTranslation();
  const [downloadError, setDownloadError] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string>();
  const { data, isLoading, isError, refetch } = useDashboardReports({
    canReadStabilityReports,
    canReadAuditTrailReports,
  });
  const reports = data?.items;

  const handleDownload = async (reportId: string) => {
    setDownloadError(false);
    setDownloadingReportId(reportId);
    try {
      const url = await downloadReport(reportId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setDownloadError(true);
    } finally {
      setDownloadingReportId(undefined);
    }
  };

  return (
    <DashboardWidget className="latest-reports-card">
      <div className="dashboard-widget__header">
        <DashboardCardTitle
          icon="document"
          title={t('dashboard.latestReports.title')}
          hint={t('dashboard.latestReports.hint')}
        />
        <SectionFeedback sectionId="latest-reports" />
      </div>
      {isLoading && <DotsLoader className="m-top-l" />}
      {isError && (
        <div className="dashboard-card__feedback" role="alert">
          <span>{t('dashboard.latestReports.loadError')}</span>
          <Button
            variant="secondary"
            onClick={() => {
              refetch();
            }}
          >
            {t('dashboard.latestReports.retry')}
          </Button>
        </div>
      )}
      {!isLoading && !isError && data?.partial && (
        <div role="status" className="dashboard-card__feedback">
          <span>{t('dashboard.widget.partial')}</span>
          <Button variant="secondary" onClick={() => refetch()}>
            {t('dashboard.latestReports.retry')}
          </Button>
        </div>
      )}
      {!isLoading && !isError && reports?.length === 0 && !data?.partial && (
        <div className="latest-reports-card__empty">
          {t('dashboard.latestReports.noReports')}
        </div>
      )}
      {!isLoading && !isError && reports && reports.length > 0 && (
        <div className="latest-reports-card__table">
          {reports.map((report) => (
            <div key={report.id} className="latest-reports-card__row">
              {report.reportType === 'stability' ? (
                <Link
                  className="latest-reports-card__name latest-reports-card__name--clickable"
                  to={{
                    search: `?sidecarRoute=stability-report%2Fdetail&id=${report.id}`,
                  }}
                >
                  <TruncatedText
                    text={report.name}
                    className="latest-reports-card__name-content"
                  />
                </Link>
              ) : (
                <TruncatedText text={report.name} className="latest-reports-card__name" />
              )}
              <CellTimestamp
                timestamp={report.createdAt}
                className="latest-reports-card__date"
              />
              <span className="latest-reports-card__size">{report.size}</span>
              <Button
                type="button"
                size="small"
                variant="icon"
                onClick={() => handleDownload(report.id)}
                disabled={downloadingReportId === report.id}
                aria-busy={downloadingReportId === report.id}
                aria-label={`${t('dashboard.latestReports.download')} ${report.name}`}
              >
                <Icon name="save" size={IconSizeClasses.Small} />
              </Button>
            </div>
          ))}
        </div>
      )}
      {downloadError && (
        <div className="latest-reports-card__download-error" role="alert">
          {t('dashboard.latestReports.downloadError')}
        </div>
      )}
    </DashboardWidget>
  );
}
